<template>
  <EDropdown
    v-bind="{ visible, 'data-test-id': 'dropdown-wrapper', ...$attrs }"
    :class="classes"
    :aria-hidden="!visible"
    :side="isDesktopUp ? side : 'left'"
    @click="onClose"
  >
    <section
      class="modal-dropdown__content"
      @click.stop="() => {}"
      @keyup.escape="onClose"
    >
      <slot />
      <footer class="modal-dropdown__footer" data-test-id="modal-dropdown-footer">
        <span>{{ title }}</span>
        <div class="modal-dropdown__close">
          <EButton
            icon="close"
            size="s"
            variant="bare"
            icon-size="s"
            data-test-id="close-button"
            @click.stop="onClose"
          />
        </div>
      </footer>
    </section>
  </EDropdown>
</template>

<script>
import { focusFocusableChild } from '~~/common/js/client/keyboard-utils';

import { toggleScrollLock, DESKTOP_DOWN } from '~~/common/js/client/screen';
import classes from '~~/common/vue/mixins/classes';
import screen from '~~/common/vue/mixins/screen';
import EDropdown from '~~/common/vue/components/dropdown';
import EButton from '~~/common/vue/components/button';

export const sizes = ['m', 'l'];
/**
 * Like Dropdown, but looks like a modal on mobile
 */
export default {
  name: 'EModalDropdown',
  components: {
    EDropdown,
    EButton,
  },
  mixins: [classes, screen],
  props: {
    title: {
      type: String,
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * one of `m`, `l`
     */
    size: {
      cssClass: true,
      type: String,
      default: sizes[0],
      validator: (size) => sizes.includes(size),
    },
    footerAlwaysShown: {
      cssClass: true,
      type: Boolean,
      default: false,
    },
    side: EDropdown.props.side,
  },
  watch: {
    visible(value) {
      // When the modal is shown, we want a fixed body
      // so the body won't scroll
      // When the modal is hidden,
      // we want to remain at the top of the scroll position
      toggleScrollLock(value, DESKTOP_DOWN);
      if (value) {
        // defer to next event loop cycle, because Vue won't have
        // rendered the children at this point. `$nextTick` didn't
        // seem to work in this case.
        setTimeout(() => focusFocusableChild(this.$el), 0);
      }
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
$sizes: (
  m: (
    default: (
      min-width: 300px,
      max-width: 300px,
    ),
    desktop: (
      min-width: 140px,
      max-width: 180px,
    ),
  ),
  l: (
    default: (
      min-width: 300px,
      max-width: 300px,
    ),
    desktop: (
      min-width: 240px,
      max-width: none,
    ),
  ),
);

.modal-dropdown {
  min-width: 0;

  @include desktop-down {
    animation: e-fade-in $timing-1s forwards;
    align-items: center;
    background: var(--color-overlay-primary);
    box-shadow: none;
    display: flex;
    height: 100%;
    justify-content: center;
    left: 0;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: $z-index-overlay;
    border-radius: 0;
  }
}

.modal-dropdown__content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: calc(100vh - #{$space-3l});

  @include desktop-down {
    @include elevation(2);

    animation: e-slide-top $timing-1s forwards;
    background: var(--color-background-primary);
    border-radius: $border-radius-l;
    margin: $space-l;
  }
}

@each $size, $config in $sizes {
  .modal-dropdown--size-#{$size} {
    .modal-dropdown__content {
      min-width: map-get(map-get($config, 'default'), 'min-width');
      max-width: map-get(map-get($config, 'default'), 'max-width');

      @include desktop {
        min-width: map-get(map-get($config, 'desktop'), 'min-width');
        max-width: map-get(map-get($config, 'desktop'), 'max-width');
      }
    }
  }
}

.modal-dropdown__close {
  display: inherit;
}

.modal-dropdown__footer {
  @include divider('bottom');

  // special value to accomodate the icon-button in xs size
  $padding-vertical: 8px;

  align-items: center;
  background: var(--color-background-secondary);
  display: flex;
  font-size: $font-l;
  justify-content: space-between;
  height: $space-4l;
  order: -1;
  padding: $padding-vertical $space-m;

  @include desktop {
    display: none;

    .modal-dropdown--footer-always-shown & {
      display: flex;

      .modal-dropdown__close {
        display: none;
      }
    }
  }
}
</style>
